import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, CalendarDays, Clock, MapPin, Users, Pencil, Trash2, ChevronLeft, ChevronRight, Phone, Video, FileText, Flag, RefreshCw, Upload, Download, Link2, Unlink, ExternalLink, Check } from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import GoogleSetupGuide from '../components/GoogleSetupGuide';

const API = process.env.REACT_APP_BACKEND_URL;
const TYPES = [
  { value: 'meeting', label: 'Meeting', icon: Users, color: 'bg-blue-500' },
  { value: 'call', label: 'Call', icon: Phone, color: 'bg-green-500' },
  { value: 'task', label: 'Task', icon: FileText, color: 'bg-purple-500' },
  { value: 'deadline', label: 'Deadline', icon: Flag, color: 'bg-red-500' },
  { value: 'holiday', label: 'Holiday', icon: CalendarDays, color: 'bg-amber-500' },
  { value: 'other', label: 'Other', icon: CalendarDays, color: 'bg-gray-500' },
];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function EventModal({ open, onClose, onSaved, editing, defaultDate }) {
  const [form, setForm] = useState({ title: '', description: '', event_type: 'meeting', start_date: '', end_date: '', all_day: false, location: '', attendees: '', color: '#3B82F6' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (editing) setForm({ title: editing.title || '', description: editing.description || '', event_type: editing.event_type || 'meeting', start_date: editing.start_date || '', end_date: editing.end_date || '', all_day: editing.all_day || false, location: editing.location || '', attendees: (editing.attendees || []).join(', '), color: editing.color || '#3B82F6' });
    else setForm({ title: '', description: '', event_type: 'meeting', start_date: defaultDate || '', end_date: '', all_day: false, location: '', attendees: '', color: '#3B82F6' });
  }, [editing, open, defaultDate]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.start_date) { toast.error('Title and date required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, attendees: form.attendees ? form.attendees.split(',').map(a => a.trim()).filter(Boolean) : [] };
      const { data } = editing
        ? await axios.put(`${API}/api/operations/events/${editing.id}`, payload)
        : await axios.post(`${API}/api/operations/events`, payload);
      toast.success(editing ? 'Updated' : 'Event created');
      onSaved(data, !!editing); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-blue-500" />{editing ? 'Edit' : 'New'} Event</DialogTitle><DialogDescription>Schedule a calendar event</DialogDescription></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} data-testid="event-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label><Select value={form.event_type} onValueChange={v => setForm(p => ({...p, event_type: v}))}><SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
            <div className="flex items-center gap-2 pt-6"><Switch checked={form.all_day} onCheckedChange={v => setForm(p => ({...p, all_day: v}))} /><Label className="text-sm">All day</Label></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start *</Label><Input type={form.all_day ? 'date' : 'datetime-local'} value={form.start_date} onChange={e => setForm(p => ({...p, start_date: e.target.value}))} data-testid="event-start" /></div>
            <div><Label>End</Label><Input type={form.all_day ? 'date' : 'datetime-local'} value={form.end_date} onChange={e => setForm(p => ({...p, end_date: e.target.value}))} /></div>
          </div>
          <div><Label>Location</Label><Input value={form.location} onChange={e => setForm(p => ({...p, location: e.target.value}))} /></div>
          <div><Label>Attendees (comma separated)</Label><Input value={form.attendees} onChange={e => setForm(p => ({...p, attendees: e.target.value}))} /></div>
          <div><Label>Notes</Label><Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={2} /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-event-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Create Event'}</Button></DialogFooter>
        </form></DialogContent></Dialog>
  );
}

function GoogleSyncPanel({ googleStatus, onConnect, onDisconnect, onSyncAll, onImport, syncing }) {
  const [setupGuideOpen, setSetupGuideOpen] = useState(false);
  if (!googleStatus.connected) {
    return (
      <>
      <Card className="border-dashed border-2 border-blue-200 bg-blue-50/30" data-testid="google-connect-card">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 24 24" className="w-5 h-5"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-800">Connect Google Calendar</h3>
            <p className="text-xs text-gray-500">Sync events between Konnect and Google Calendar</p>
          </div>
          {/* v2.5.927 — Setup guide button for admins hitting "Google credentials not configured" */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSetupGuideOpen(true)}
            className="gap-1.5 text-xs"
            data-testid="google-setup-guide-btn"
          >
            Setup Steps
          </Button>
          <Button onClick={onConnect} className="bg-blue-600 hover:bg-blue-700 gap-1.5" size="sm" data-testid="google-connect-btn">
            <Link2 className="h-3.5 w-3.5" /> Connect
          </Button>
        </CardContent>
      </Card>
      <GoogleSetupGuide open={setupGuideOpen} onClose={() => setSetupGuideOpen(false)} product="calendar" />
    </>
    );
  }

  return (
    <Card className="border-green-200 bg-green-50/30" data-testid="google-sync-panel">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
            <Check className="h-4 w-4 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-800">Google Calendar Connected</h3>
              <Badge className="bg-green-100 text-green-700 text-[9px]">Active</Badge>
            </div>
            <p className="text-xs text-gray-500 truncate">{googleStatus.google_email}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="gap-1 text-xs h-8" onClick={onSyncAll} disabled={syncing} data-testid="sync-to-google-btn">
              <Upload className="h-3 w-3" /> Push to Google
            </Button>
            <Button variant="outline" size="sm" className="gap-1 text-xs h-8" onClick={onImport} disabled={syncing} data-testid="import-from-google-btn">
              <Download className="h-3 w-3" /> Import from Google
            </Button>
            <Button variant="ghost" size="sm" className="gap-1 text-xs h-8 text-red-500 hover:text-red-600" onClick={onDisconnect} data-testid="google-disconnect-btn">
              <Unlink className="h-3 w-3" /> Disconnect
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [defaultDate, setDefaultDate] = useState('');
  const [googleStatus, setGoogleStatus] = useState({ connected: false });
  const [syncing, setSyncing] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/operations/events`);
      setEvents(data);
    } catch { toast.error('Failed to load events'); }
    finally { setLoading(false); }
  }, []);

  const fetchGoogleStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/google/auth/status`);
      setGoogleStatus(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchGoogleStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected') === 'true') {
      toast.success('Google Calendar connected successfully!');
      fetchGoogleStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('google_error')) {
      toast.error(`Google connection failed: ${params.get('google_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchEvents, fetchGoogleStatus]);

  const handleSaved = (e, isUpdate) => { if (isUpdate) setEvents(p => p.map(x => x.id === e.id ? e : x)); else setEvents(p => [e, ...p]); };
  const handleDelete = async (id) => { if (!window.confirm('Delete event?')) return; try { await axios.delete(`${API}/api/operations/events/${id}`); setEvents(p => p.filter(e => e.id !== id)); toast.success('Deleted'); } catch { toast.error('Failed'); } };

  const connectGoogle = async () => {
    try {
      const { data } = await axios.get(`${API}/api/google/auth/login`);
      window.location.href = data.authorization_url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to start Google auth'); }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google Calendar?')) return;
    try {
      await axios.delete(`${API}/api/google/auth/disconnect`);
      setGoogleStatus({ connected: false });
      toast.success('Google Calendar disconnected');
    } catch { toast.error('Failed to disconnect'); }
  };

  const syncAllToGoogle = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/api/google/calendar/sync-all`, {});
      toast.success(`Pushed ${data.synced} events to Google Calendar`);
      fetchEvents();
    } catch (err) { toast.error(err.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const importFromGoogle = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/api/google/calendar/import`, {});
      toast.success(`Imported ${data.imported} events from Google Calendar`);
      fetchEvents();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setSyncing(false); }
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.start_date && e.start_date.startsWith(dateStr));
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1"><div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><CalendarDays className="h-5 w-5 text-blue-500" /></div><div><h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Calendar<PageInfoTooltip pageKey="calendar" /></h1><p className="text-xs text-gray-400">{events.length} events</p></div></div>
        <Button onClick={() => { setEditing(null); setDefaultDate(''); setModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 h-9 gap-1.5" data-testid="new-event-btn"><Plus className="h-4 w-4" /> New Event</Button>
      </div>

      <GoogleSyncPanel
        googleStatus={googleStatus}
        onConnect={connectGoogle}
        onDisconnect={disconnectGoogle}
        onSyncAll={syncAllToGoogle}
        onImport={importFromGoogle}
        syncing={syncing}
      />

      <Card className="border-gray-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="icon" onClick={prevMonth}><ChevronLeft className="h-5 w-5" /></Button>
            <h2 className="text-base font-heading font-bold text-gray-800">{MONTHS[month]} {year}</h2>
            <Button variant="ghost" size="icon" onClick={nextMonth}><ChevronRight className="h-5 w-5" /></Button>
          </div>
          <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-xl overflow-hidden" data-testid="calendar-grid">
            {DAYS.map(d => <div key={d} className="bg-gray-50 p-2 text-center text-[10px] font-semibold text-gray-500 uppercase">{d}</div>)}
            {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} className="bg-white p-2 min-h-[80px]" />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayEvents = getEventsForDay(day);
              const isToday = dateStr === todayStr;
              return (
                <div key={day} className={`bg-white p-1.5 min-h-[80px] cursor-pointer hover:bg-blue-50/50 transition-colors ${isToday ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
                  onClick={() => { setEditing(null); setDefaultDate(dateStr); setModalOpen(true); }} data-testid={`cal-day-${day}`}>
                  <span className={`text-xs font-medium ${isToday ? 'bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center' : 'text-gray-700'}`}>{day}</span>
                  <div className="mt-1 space-y-0.5">
                    {dayEvents.slice(0, 3).map(ev => {
                      const typeInfo = TYPES.find(t => t.value === ev.event_type) || TYPES[5];
                      const isGoogleEvent = !!ev.google_event_id;
                      return (
                        <div key={ev.id} className={`text-[9px] text-white px-1 py-0.5 rounded ${typeInfo.color} truncate flex items-center gap-0.5`} title={ev.title}>
                          {isGoogleEvent && <svg viewBox="0 0 24 24" className="w-2 h-2 flex-shrink-0 fill-white opacity-80"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/></svg>}
                          {ev.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && <span className="text-[9px] text-gray-400">+{dayEvents.length - 3} more</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {events.length > 0 && (
        <div>
          <h2 className="text-sm font-heading font-bold text-gray-700 mb-2">Upcoming Events</h2>
          <div className="space-y-2">
            {events.slice(0, 10).map(ev => {
              const typeInfo = TYPES.find(t => t.value === ev.event_type) || TYPES[5];
              const Icon = typeInfo.icon;
              const isGoogleSynced = !!ev.google_event_id;
              return (
                <Card key={ev.id} className="border-gray-200" data-testid={`event-row-${ev.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg ${typeInfo.color} flex items-center justify-center flex-shrink-0`}><Icon className="h-4 w-4 text-white" /></div>
                    <div className="flex-1 min-w-0"><h3 className="text-sm font-semibold text-gray-900">{ev.title}</h3><p className="text-[10px] text-gray-400">{ev.start_date} {ev.location && `· ${ev.location}`}</p></div>
                    {isGoogleSynced && <Badge className="bg-blue-50 text-blue-600 text-[9px] gap-0.5"><svg viewBox="0 0 24 24" className="w-2.5 h-2.5 fill-blue-500"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/></svg>Synced</Badge>}
                    <Badge variant="outline" className="text-[9px]">{typeInfo.label}</Badge>
                    <div className="flex gap-1"><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(ev); setModalOpen(true); }}><Pencil className="h-3.5 w-3.5 text-gray-400" /></Button><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(ev.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button></div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      <EventModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} defaultDate={defaultDate} />
    </div>
  );
}
